/**
 * Client-side activity and error logging.
 *
 * Why this exists: volunteer check-in writes straight to Firestore from the
 * browser, so nothing about it reaches Cloud Logging. When the 9am check-in on
 * 2026-08-30 "didn't work on the desktop" there was no record of what the
 * operator saw, what failed, or on which device — only the check-in documents
 * that did land. This module records the operator-visible story so the next
 * incident is diagnosable instead of reconstructed.
 *
 * Two rules govern everything here:
 *
 *   1. Logging MUST NOT break the thing it is logging. Every entry point
 *      swallows its own errors and returns without throwing. Callers are not
 *      expected to await.
 *   2. Logging MUST NOT flood. A failing page can fire the same error
 *      thousands of times; entries are deduplicated and rate limited.
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from './config';
import { getViewAs } from '../utils/viewAs';
import APP_VERSION from '../version';

const COLLECTION = 'activityLogs';

// Entry types.
export const LOG_TYPE = {
  ACTION: 'action',   // something a user did
  ERROR: 'error',     // something that went wrong
};

// Broad area of the app, used as the primary filter in the admin viewer.
export const LOG_CATEGORY = {
  VOLUNTEER_CHECKIN: 'volunteer_checkin',
  GUEST_CHECKIN: 'guest_checkin',
  CHECKOUT: 'checkout',
  AUTH: 'auth',
  ADMIN: 'admin',
  APP: 'app',
};

// Outcome of an action. 'partial' matters: a batch check-in where some
// volunteers were written and others failed is neither a success nor a
// failure, and reporting it as either is what made the incident confusing.
export const LOG_STATUS = {
  SUCCESS: 'success',
  PARTIAL: 'partial',
  FAILURE: 'failure',
  INFO: 'info',
};

// ---------------------------------------------------------------------------
// Flood control
// ---------------------------------------------------------------------------

const MAX_ENTRIES_PER_MINUTE = 30;
const DEDUPE_WINDOW_MS = 10000;
const MAX_STRING = 1000;
const MAX_STACK = 2000;

let windowStartedAt = 0;
let windowCount = 0;
const recentKeys = new Map(); // dedupe key -> last logged timestamp

// True when this entry is allowed through. `key` identifies "the same thing
// happening again" — an identical error from a loop is dropped, while a real
// second occurrence ten seconds later is kept.
const allow = (key) => {
  const now = Date.now();

  if (now - windowStartedAt > 60000) {
    windowStartedAt = now;
    windowCount = 0;
  }
  if (windowCount >= MAX_ENTRIES_PER_MINUTE) return false;

  const last = recentKeys.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;

  // Keep the dedupe map from growing without bound on a long-lived tab.
  if (recentKeys.size > 200) {
    for (const [k, t] of recentKeys) {
      if (now - t > DEDUPE_WINDOW_MS) recentKeys.delete(k);
    }
  }

  recentKeys.set(key, now);
  windowCount += 1;
  return true;
};

// ---------------------------------------------------------------------------
// Context capture
// ---------------------------------------------------------------------------

const truncate = (value, max = MAX_STRING) => {
  if (value === null || value === undefined) return null;
  const s = typeof value === 'string' ? value : String(value);
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

/**
 * Describes the machine the operator is sitting at. This is the field that
 * would have answered "why did it work on the iPad but not the desktop?"
 * without anyone having to guess.
 */
const describeDevice = () => {
  try {
    const ua = navigator.userAgent || '';
    const touch = (navigator.maxTouchPoints || 0) > 0;
    // iPadOS 13+ reports a Macintosh UA, so touch points are what actually
    // separates an iPad from a desktop Mac.
    const isIPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && touch);
    const isTablet = isIPad || /Tablet|Android(?!.*Mobile)/.test(ua);
    const isPhone = /iPhone|Android.*Mobile|Windows Phone/.test(ua);

    let browser = 'Unknown';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/OPR\//.test(ua)) browser = 'Opera';
    else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = 'Chrome';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua)) browser = 'Safari';

    return {
      formFactor: isPhone ? 'phone' : isTablet ? 'tablet' : 'desktop',
      browser,
      platform: truncate(navigator.platform || '', 60),
      touch,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      online: navigator.onLine !== false,
      // Effective connection type, where the browser reports it. A '2g' or
      // 'slow-2g' reading next to a failed write is a strong signal.
      connection: navigator.connection?.effectiveType || null,
      userAgent: truncate(ua, 300),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    };
  } catch {
    return null;
  }
};

// Strip undefined values — Firestore rejects them — and cap nesting cost by
// stringifying anything that isn't a primitive, array, or plain object.
const sanitize = (value, depth = 0) => {
  if (value === null || value === undefined) return null;
  if (depth > 4) return truncate(JSON.stringify(value), 300);

  const t = typeof value;
  if (t === 'string') return truncate(value);
  if (t === 'number') return Number.isFinite(value) ? value : String(value);
  if (t === 'boolean') return value;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.slice(0, 50).map((v) => sanitize(v, depth + 1));

  if (t === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue;
      out[k] = sanitize(v, depth + 1);
    }
    return out;
  }
  return truncate(String(value));
};

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

/**
 * Write one entry. Fire-and-forget: never throws, never rejects, and the
 * returned promise resolves whether or not the write landed.
 *
 * @param {Object} entry
 * @param {string} entry.type      LOG_TYPE value
 * @param {string} entry.category  LOG_CATEGORY value
 * @param {string} entry.action    short machine-ish name, e.g. 'volunteer_check_in'
 * @param {string} [entry.status]  LOG_STATUS value
 * @param {string} [entry.message] human-readable summary
 * @param {Object} [entry.details] structured extras (sanitized)
 * @param {Error|Object} [entry.error] the failure, when this is an error entry
 */
export const logActivity = async (entry) => {
  try {
    const {
      type = LOG_TYPE.ACTION,
      category = LOG_CATEGORY.APP,
      action,
      status = LOG_STATUS.INFO,
      message = '',
      details,
      error,
    } = entry || {};

    if (!action) return;

    const dedupeKey = `${type}|${category}|${action}|${status}|${truncate(message, 120)}`;
    if (!allow(dedupeKey)) return;

    // Always the real signed-in user, never a "view as" identity: the log is
    // the audit trail, and rules require userId to match the caller's uid.
    const user = auth.currentUser;
    // Security rules require the entry's userId to match the caller's uid, so
    // an entry written before sign-in would be rejected anyway. Drop it here
    // rather than firing a request that can only fail.
    if (!user?.uid) return;

    const doc = {
      type,
      category,
      action,
      status,
      message: truncate(message),
      details: details ? sanitize(details) : null,

      userId: user.uid,
      userName: user.displayName || null,
      userEmail: user.email || null,
      viewingAs: (() => {
        const v = getViewAs(user.uid);
        return v ? { uid: v.uid, name: v.displayName || v.email || null, role: v.role || null } : null;
      })(),

      route: truncate(window.location?.pathname || '', 200),
      appVersion: APP_VERSION,
      device: describeDevice(),

      // Client clock, kept alongside the server timestamp. A large gap between
      // the two is itself diagnostic (a device with a wrong clock, or a write
      // that sat queued offline before it flushed).
      clientTime: new Date().toISOString(),
      createdAt: serverTimestamp(),
    };

    if (error) {
      doc.errorName = truncate(error.name || 'Error', 120);
      doc.errorCode = truncate(error.code || null, 120);
      doc.errorMessage = truncate(error.message || String(error));
      doc.errorStack = truncate(error.stack || null, MAX_STACK);
    }

    await addDoc(collection(db, COLLECTION), doc);
  } catch (err) {
    // Deliberately swallowed. A logging failure must never surface to the
    // operator or abort the action being logged.
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[activityLog] failed to write entry:', err);
    }
  }
};

/** Convenience wrapper for a user action. */
export const logAction = (category, action, status, message, details) =>
  logActivity({ type: LOG_TYPE.ACTION, category, action, status, message, details });

/** Convenience wrapper for a failure. */
export const logError = (category, action, error, message, details) =>
  logActivity({
    type: LOG_TYPE.ERROR,
    category,
    action,
    status: LOG_STATUS.FAILURE,
    message: message || error?.message || 'Unexpected error',
    details,
    error,
  });

// ---------------------------------------------------------------------------
// Global capture
// ---------------------------------------------------------------------------

let installed = false;

/**
 * Install window-level handlers so an uncaught error or a rejected promise is
 * recorded even where no try/catch reaches it. Safe to call more than once.
 */
export const installGlobalErrorLogging = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    // Resource load failures (img/script) surface here with no `error` object
    // and would otherwise log as an empty, useless entry.
    if (!event.error && !event.message) return;
    logActivity({
      type: LOG_TYPE.ERROR,
      category: LOG_CATEGORY.APP,
      action: 'uncaught_error',
      status: LOG_STATUS.FAILURE,
      message: event.message || 'Uncaught error',
      error: event.error || { name: 'Error', message: event.message },
      details: {
        source: event.filename || null,
        line: event.lineno ?? null,
        column: event.colno ?? null,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const error =
      reason instanceof Error
        ? reason
        : { name: 'UnhandledRejection', message: truncate(JSON.stringify(reason) || String(reason), 500) };
    logActivity({
      type: LOG_TYPE.ERROR,
      category: LOG_CATEGORY.APP,
      action: 'unhandled_rejection',
      status: LOG_STATUS.FAILURE,
      message: error.message || 'Unhandled promise rejection',
      error,
    });
  });

  // A dropped connection is the most likely cause of a check-in that looks
  // like it failed but actually landed, so record the transition itself.
  window.addEventListener('offline', () => {
    logActivity({
      type: LOG_TYPE.ERROR,
      category: LOG_CATEGORY.APP,
      action: 'connection_lost',
      status: LOG_STATUS.FAILURE,
      message: 'Browser went offline',
    });
  });

  window.addEventListener('online', () => {
    logActivity({
      type: LOG_TYPE.ACTION,
      category: LOG_CATEGORY.APP,
      action: 'connection_restored',
      status: LOG_STATUS.INFO,
      message: 'Browser came back online',
    });
  });
};
